/**
 * PlateConversationEditor - Shared Plate.js editor with multi-entity mention support.
 *
 * Configurable for single-line (DashboardHeader) and multi-line (ConversationInput) variants.
 * Supports all entity types: users, contacts, companies, and deals.
 *
 * Content format:
 * - Parses: <Entity>, <NamedEntity>, <mention>, and <@uuid|Name> formats
 * - Outputs: <Entity type="..." entity-id="...">Name</Entity>
 */

import { MentionInputPlugin, MentionPlugin } from '@platejs/mention/react';
import type { Descendant, TElement, Value } from '@platejs/slate';
import { KEYS } from 'platejs';
import { Plate, PlateContent, usePlateEditor } from 'platejs/react';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { EMOJI_EDITOR_PLUGINS } from '@/lib/crm/components/emoji-editor-plugins';
import { EntityElement } from '@/lib/crm/components/EntityElement';
import { createEntityInputElement } from '@/lib/crm/components/EntityInputElement';
import { insertReplacementTextPlugin } from '@/lib/crm/components/insert-replacement-text-plugin';
import { normalizeEntityType } from '@/lib/search';
import type { EntityType } from '@/lib/search/types';
import { cx } from '@/utils/cx';

/**
 * Combined regex for finding any mention format:
 * - <Entity type="X" entity-id="Y">Name</Entity>
 * - <Entity type="X" id="Y">Name</Entity>
 * - <NamedEntity type="X" entity-id="Y">Name</NamedEntity> (legacy)
 * - <NamedEntity type="X" id="Y">Name</NamedEntity> (legacy)
 * - <mention type="X" entity-id="Y">Name</mention> (legacy)
 * - <mention type="X" id="Y">Name</mention> (legacy)
 * - <@uuid|Display Name> (legacy Slack-style)
 */
const ALL_MENTIONS_REGEX =
  /(?:<(?:mention|NamedEntity|Entity) type="(\w+)" (?:entity-id|id)="([^"]+)">([^<]*)<\/(?:mention|NamedEntity|Entity)>|<@([^|>]+)\|([^>]+)>)/gi;

export interface PlateConversationEditorProps {
  /** Current value (plain text with mention tags) */
  value: string;
  /** Called when content changes */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Single-line (DashboardHeader) or multi-line (ConversationInput) */
  variant: 'single-line' | 'multi-line';
  /** Called when Enter is pressed (without Shift) */
  onSubmit?: () => void;
  /** Entity types to allow for mentions (defaults to all 4) */
  entityTypes?: EntityType[];
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Whether to auto-focus on mount */
  autoFocus?: boolean;
}

interface ParsedMentionNode {
  type: typeof KEYS.mention;
  value: string;
  displayName: string;
  entityType: string;
  children: [{ text: string }];
}

/**
 * Parse plain text with mention tags into Plate Value nodes.
 * Handles all three formats: <NamedEntity>, <mention>, and <@uuid|Name>
 */
function parseContentToNodes(content: string): Value {
  if (!content || !content.trim()) {
    return [{ type: 'p', children: [{ text: '' }] }];
  }

  const nodes: Value = [];
  const lines = content.split('\n');

  for (const line of lines) {
    const children: Descendant[] = [];
    let lastIndex = 0;
    let match;

    // Reset regex state
    ALL_MENTIONS_REGEX.lastIndex = 0;

    while ((match = ALL_MENTIONS_REGEX.exec(line)) !== null) {
      // Add text before the mention
      if (match.index > lastIndex) {
        children.push({ text: line.slice(lastIndex, match.index) });
      }

      // Determine which format matched
      // Groups 1-3: XML format (type, id, displayName)
      // Groups 4-5: Slack format (entityId, displayName)
      const isXmlFormat = match[1] !== undefined;
      const rawType = isXmlFormat ? match[1] : 'user';
      // Normalize to canonical ahoy_*_entity types
      const entityType = normalizeEntityType(rawType);
      const entityId = isXmlFormat ? match[2] : match[4];
      const displayName = isXmlFormat ? match[3] : match[5];

      // Add mention node with stable key
      const mentionNode: ParsedMentionNode = {
        type: KEYS.mention,
        value: entityId,
        displayName,
        entityType,
        children: [{ text: '' }],
      };

      children.push(mentionNode as unknown as TElement);

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last mention
    if (lastIndex < line.length) {
      children.push({ text: line.slice(lastIndex) });
    }

    // Ensure at least empty text if no children
    if (children.length === 0) {
      children.push({ text: '' });
    }

    nodes.push({ type: 'p', children });
  }

  return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
}

/**
 * Serialize Plate Value nodes back to plain text with Entity tags.
 * Always outputs <Entity> format regardless of input format.
 */
function serializeNodesToContent(nodes: Value): string {
  const lines: string[] = [];

  for (const node of nodes) {
    if (!node || typeof node !== 'object') {
      continue;
    }

    const nodeWithChildren = node as { type?: string; children?: Descendant[] };
    if (!nodeWithChildren.children) {
      continue;
    }

    let lineText = '';

    for (const child of nodeWithChildren.children) {
      if (!child || typeof child !== 'object') {
        continue;
      }

      const childNode = child as {
        type?: string;
        text?: string;
        value?: string;
        displayName?: string;
        entityType?: string;
      };

      if (childNode.type === KEYS.mention) {
        // Serialize to Entity format with canonical types
        const rawType = childNode.entityType || 'user';
        const type = normalizeEntityType(rawType);
        const entityId = childNode.value || '';
        const displayName = childNode.displayName || '';
        lineText += `<Entity type="${type}" entity-id="${entityId}">${displayName}</Entity>`;
      } else if ('text' in childNode) {
        // Regular text node
        lineText += childNode.text || '';
      }
    }

    lines.push(lineText);
  }

  return lines.join('\n');
}

/**
 * Default entity types the conversation mention picker offers when a caller
 * doesn't pass `entityTypes`. Hoisted to module scope so the default argument
 * is the SAME array reference every render — an inline literal default would
 * hand `EntityInputComponent`'s useMemo a fresh dependency each render,
 * defeating it. FRONTEND_PATTERNS.md "Hoist Constants" rule.
 */
const DEFAULT_ENTITY_TYPES: EntityType[] = [
  'ahoy_user',
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
];

/**
 * PlateConversationEditor component.
 */
export function PlateConversationEditor({
  value,
  onChange,
  placeholder = 'Type a message',
  readOnly = false,
  variant,
  onSubmit,
  entityTypes = DEFAULT_ENTITY_TYPES,
  className,
  style,
  autoFocus = false,
}: PlateConversationEditorProps) {
  // Track last emitted value to prevent unnecessary onChange calls
  const lastEmittedValueRef = useRef<string>(value || '');

  // Parse incoming value to Plate nodes - memoized
  const initialValue = useMemo(() => parseContentToNodes(value || ''), [value]);

  // Create the EntityInputElement component with entity types
  const EntityInputComponent = useMemo(
    () => createEntityInputElement(entityTypes),
    [entityTypes]
  );

  // Build plugins list
  const plugins = useMemo(() => {
    return [
      insertReplacementTextPlugin,
      MentionPlugin.configure({
        options: {
          trigger: '@',
          insertSpaceAfterMention: true,
        },
      }).withComponent(EntityElement),
      MentionInputPlugin.withComponent(EntityInputComponent),
      ...EMOJI_EDITOR_PLUGINS,
    ];
  }, [EntityInputComponent]);

  // Create editor with plugins
  const editor = usePlateEditor({
    plugins,
    value: initialValue,
  });

  // Ref for the editable content element - declared before effects that reference it
  const contentRef = useRef<HTMLDivElement>(null);
  // One-shot guard: fires autofocus exactly once per editor instance
  const hasAutoFocusedRef = useRef(false);

  // Sync editor state when value prop changes externally (e.g., parent clears input after submit)
  // Always sync regardless of readOnly - the user should see the updated value even if they can't edit.
  // readOnly prevents user INPUT, but programmatic VALUE changes should always be reflected.
  useEffect(() => {
    const normalizedValue = value || '';
    // Only sync if value differs from what we last emitted (external change)
    if (normalizedValue !== lastEmittedValueRef.current) {
      // Capture focus BEFORE setValue — setValue replaces the document and
      // drops the caret/selection.
      const wasFocused =
        !!contentRef.current &&
        contentRef.current.contains(document.activeElement);
      editor.tf.setValue(parseContentToNodes(normalizedValue));
      lastEmittedValueRef.current = normalizedValue;
      // When a keystroke raced the swap (editor was focused, not read-only),
      // restore focus and place the caret at end so typing keeps appending.
      // Skip when unfocused or read-only so we never steal focus (e.g. the
      // proposals submit-clear path sets value='' AND readOnly=true).
      if (wasFocused && !readOnly) {
        editor.tf.focus();
        editor.tf.select(editor.api.end([]));
      }
    }
  }, [value, editor, readOnly]);

  // Programmatically focus and set selection on mount.
  // autoFocus prop alone doesn't work reliably with Plate - need explicit selection.
  const shouldAutoFocus = variant === 'single-line' || autoFocus;
  useEffect(() => {
    if (shouldAutoFocus && contentRef.current && !hasAutoFocusedRef.current) {
      // Use requestAnimationFrame to ensure Plate is fully initialized
      const frameId = requestAnimationFrame(() => {
        hasAutoFocusedRef.current = true;
        // Single focus owner (autoFocus removed from <PlateContent>). Always
        // place the caret at end-of-content so a non-empty mount (skeleton→
        // loaded handoff) appends; for empty content end == start.
        editor.tf.select(editor.api.end([]));
        contentRef.current?.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [shouldAutoFocus, editor]);

  // Handle changes - memoized with useCallback
  const handleChange = useCallback(
    (newValue: Value) => {
      if (readOnly) {
        return;
      }

      const content = serializeNodesToContent(newValue);

      // Only emit if content actually changed
      if (content !== lastEmittedValueRef.current) {
        lastEmittedValueRef.current = content;
        onChange(content);
      }
    },
    [readOnly, onChange]
  );

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // For single-line variant, prevent newlines entirely
      if (variant === 'single-line' && e.key === 'Enter') {
        e.preventDefault();
        // Ignore a held-down Enter's auto-repeat: a repeat landing right
        // after this editor gains focus (e.g. a route seed that autofocuses
        // it — see useNewConversationComposer) must not submit content the
        // user never meant to send from here.
        if (!e.repeat) {
          onSubmit?.();
        }
        return;
      }

      // For multi-line variant:
      // - Enter (without Shift) submits
      // - Shift+Enter inserts newline
      if (variant === 'multi-line' && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        // See the single-line branch above for why repeats are ignored.
        if (!e.repeat) {
          onSubmit?.();
        }
        return;
      }

      // Escape closes any open menus/dropdowns (handled by Plate internally)
    },
    [variant, onSubmit]
  );

  // Handle click/tap to position cursor at start when empty
  // Uses onClick for cross-platform compatibility (iOS Safari, touch laptops, hybrid devices)
  const handleClick = useCallback(() => {
    // Only reposition cursor when editor is empty and not read-only
    if (!value?.trim() && !readOnly) {
      // Use requestAnimationFrame to ensure Slate has processed the click
      requestAnimationFrame(() => {
        editor.tf.select({ path: [0, 0], offset: 0 });
      });
    }
    // If editor has content, let Slate's default behavior position cursor at click location
  }, [value, readOnly, editor]);

  const containerClasses = cx(
    // Base classes for all variants
    // - text-left ensures proper alignment even when parent has text-center
    // - !caret-black/white with !important ensures cursor is visible (overrides Plate's styles)
    // - placeholder uses pointer-events-none to not interfere with caret
    // - NO font-size here - let parent control via className
    'w-full text-left text-primary !caret-black dark:!caret-white focus:outline-none',
    // Placeholder styling - must not cover caret
    '[&_[data-slate-placeholder]]:!text-fg-quaternary [&_[data-slate-placeholder]]:!opacity-100 [&_[data-slate-placeholder]]:!pointer-events-none',
    variant === 'single-line'
      ? // Force single-line layout WITHOUT flex (flex breaks caret positioning):
        // - whitespace-nowrap on the container prevents ALL wrapping
        // - [&_p]:inline prevents paragraph line breaks
        // - overflow-x-auto for horizontal scrolling when content exceeds width
        // - NO padding/height here - let parent control via className
        // - [&_[data-slate-node="element"]]:relative + -top-px nudges tokens up 1px for alignment
        // - Placeholder override: Slate renders it position:absolute, which means it doesn't
        //   affect container height. On small screens the placeholder wraps but overflow-y-hidden
        //   clips the bottom lines. A CSS @media rule in design-system.css (class: plate-editor-single-line)
        //   overrides to position:relative on small screens (<640px) so the container grows to fit
        //   the wrapped placeholder text. On wider screens, Slate's default absolute positioning
        //   keeps the container at single-line height.
        'plate-editor-single-line whitespace-nowrap overflow-x-auto overflow-y-hidden [&_p]:inline [&_[data-slate-node="element"]]:relative [&_[data-slate-node="element"]]:-top-px'
      : // Multi-line: plate-editor-multi-line enables placeholder wrapping on small screens
        'plate-editor-multi-line',
    className
  );

  // Compute isEmpty state for data-empty attribute
  const isEmpty = !value?.trim();

  // Merge incoming style with variant-specific styles
  // Note: style prop is used to override Plate's inline min-height: 24px
  const editorStyle: React.CSSProperties = {
    ...style,
  };

  return (
    <Plate editor={editor} onChange={({ value }) => handleChange(value)}>
      <PlateContent
        ref={contentRef}
        readOnly={readOnly}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        className={containerClasses}
        style={editorStyle}
        aria-label={
          variant === 'single-line' ? 'Search input' : 'Message input'
        }
        data-empty={isEmpty}
      />
    </Plate>
  );
}
